import React, { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Button, Typography, theme } from 'antd';
import {
  DashboardOutlined,
  ToolOutlined,
  UserOutlined,
  AppstoreOutlined,
  CommentOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  BarChartOutlined,
  LogoutOutlined,
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RocketOutlined,
  ShoppingOutlined,
  CreditCardOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const AdminLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { token: themeToken } = theme.useToken();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    {
      key: '/admin',
      icon: <DashboardOutlined />,
      label: t('adminPanel.dashboard'),
    },
    {
      key: '/admin/users',
      icon: <UserOutlined />,
      label: t('adminPanel.users'),
    },
    {
      key: '/admin/tools-manage',
      icon: <ToolOutlined />,
      label: t('adminPanel.toolManage'),
    },
    {
      key: '/admin/categories',
      icon: <AppstoreOutlined />,
      label: t('adminPanel.categories'),
    },
    {
      key: '/admin/reviews',
      icon: <CommentOutlined />,
      label: t('adminPanel.reviews'),
    },
    {
      key: '/admin/feedback',
      icon: <MessageOutlined />,
      label: t('adminPanel.feedback'),
    },
    {
      key: '/admin/submissions',
      icon: <RocketOutlined />,
      label: t('adminPanel.submissions'),
    },
    {
      key: '/admin/orders',
      icon: <ShoppingOutlined />,
      label: t('adminPanel.orders'),
    },
    {
      key: '/admin/payment-config',
      icon: <CreditCardOutlined />,
      label: '支付配置',
    },
    {
      key: '/admin/tools',
      icon: <SafetyCertificateOutlined />,
      label: t('adminPanel.compliance'),
    },
    {
      key: '/admin/stats',
      icon: <BarChartOutlined />,
      label: t('adminPanel.stats'),
    },
    {
      key: '/admin/assets',
      icon: <PictureOutlined />,
      label: '资产管理',
    },
  ];

  const userMenuItems = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: t('adminPanel.backToSite'),
      onClick: () => navigate('/'),
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('nav.logout'),
      onClick: handleLogout,
    },
  ];

  // Determine selected key
  const selectedKey = menuItems.find(item => {
    if (item.key === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(item.key);
  })?.key || '/admin';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={220}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: '#001529',
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/admin')}
        >
          <DashboardOutlined style={{ fontSize: 22, color: '#1677ff' }} />
          {!collapsed && (
            <Text strong style={{ fontSize: 16, color: '#fff', whiteSpace: 'nowrap' }}>
              {t('adminPanel.title')}
            </Text>
          )}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0, marginTop: 8 }}
        />
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 80 : 220, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16 }}
          />

          <Space>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  icon={<UserOutlined />}
                  style={{ backgroundColor: themeToken.colorPrimary }}
                />
                <Text>{user?.username}</Text>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: 24,
            minHeight: 'calc(100vh - 112px)',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
